/**
 * SCHEDULE's two dialogs — desktop (migration 3071; DESIGN-CHANGELOG
 * 2026-10-14). `design/GT Schedule.dc.html`, the `inModals` half, mounted at
 * the root so they open over the night page:
 *
 *  - NOMINATE — GAME NIGHT's rail (Search Box, ALL/S/A/B/C with counts, the
 *    cover grid) over the Range Slider and ADD. FIXED height
 *    (`min(720px,100%)`), the body scrolls: a tab or a search must never
 *    resize the window around the user (design CLAUDE.md, 2026-10-14).
 *  - The game modal — cover + tier, the Steam line, nominator · age, the
 *    window chip, GAME PAGE ▸ / STEAM ↗, the PARTY table (each player's call
 *    and a coin where their quarter is down), PUT MY QUARTER DOWN; the
 *    nominator gets EDIT RANGE and the armed ✕ REMOVE instead.
 *
 * Both close on the overlay, on ✕ and on Escape.
 */

import { CoverArt } from '../../components/CoverArt.tsx';
import { PixelButton } from '../../components/PixelButton.tsx';
import { QuarterIcon } from '../../components/QuarterIcon.tsx';
import { CoinOff, RangeSlider, SchFace } from '../../components/SchedKit.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import { useArt } from '../../components/useArt.ts';
import { ArmedLabel } from '../../components/Wheel.tsx';
import { useQd } from '../../store/index.ts';
import { scheduleVM } from '../../store/selectors.schedule.ts';
import type { SchGameVM, SchNightVM } from '../../store/selectors.schedule.ts';
import { useEscape } from '../components/Modal.tsx';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';
const SANS = 'var(--gt-font-sans)';

const stop = (e: { stopPropagation: () => void }): void => e.stopPropagation();

/** Mounted by `DesktopApp`; renders whichever of the two is open on the night page. */
export default function SchedModals(): JSX.Element | null {
  const s = useQd();
  const on = s.screen === 'schedDay' && !!s.schDay && (s.schNomOpen || s.schGame !== 0);
  if (!on) return null;
  const d = scheduleVM(s).day;
  if (!d) return null;
  return (
    <>
      {d.gOn && d.g ? <GameModal g={d.g} /> : null}
      {d.nomOpen ? <NominateModal d={d} /> : null}
    </>
  );
}

function GameModal({ g }: { g: SchGameVM }): JSX.Element {
  const s = useQd();
  const close = () => s.schCloseGame();
  useEscape(close);
  const art = useArt(g.id + 'p', g.coverCands);
  return (
    <div onClick={close} data-screen-label="Schedule game" style={{ position: 'fixed', inset: 0, zIndex: 81, background: 'var(--gt-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={stop} style={{ width: 560, maxWidth: '100%', maxHeight: '100%', overflow: 'hidden', background: 'var(--gt-card)', border: '1px solid var(--gt-border)', boxShadow: 'var(--gt-shadow-modal)', display: 'flex', flexDirection: 'column', cursor: 'default' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderBottom: '1px solid var(--gt-line)', flex: 'none' }}>
          <span style={{ fontFamily: SANS, fontSize: 15, fontWeight: 600, color: 'var(--gt-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.nm}</span>
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-muted)', whiteSpace: 'nowrap' }}>{g.dayTxt}</span>
          <span onClick={close} className="d-hov-acc2" style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 13, color: 'var(--gt-text-5)', cursor: 'pointer', padding: '2px 4px' }}>
            ✕
          </span>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14, padding: '16px 18px' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ position: 'relative', width: 84, height: 114, flex: 'none', boxSizing: 'border-box', border: '1px solid var(--gt-ink)', background: 'var(--gt-tile)', overflow: 'hidden' }}>
              <CoverArt style={{ position: 'absolute', inset: 0 }} fs={11} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
              <div style={{ position: 'absolute', left: 0, top: 0, zIndex: 2, background: 'var(--gt-ink)', color: g.tClr, fontFamily: PIXEL, fontSize: 7, padding: '4px 4px 2px' }}>{g.tLet}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', color: 'var(--gt-muted)', lineHeight: 1.7 }}>{g.sub}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <SchFace f={g.byF} px={14} fs={6} />
                  <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.05em', color: 'var(--gt-text-4)' }}>{g.byTxt}</span>
                </div>
                <span style={{ border: '1px solid ' + g.rBd, color: g.rClr, fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.05em', padding: '4px 7px', whiteSpace: 'nowrap' }}>{g.rangeFull}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div
                  onClick={() => s.openGame(g.id)}
                  className="d-hov-acc-box"
                  style={{ display: 'flex', alignItems: 'center', height: 30, padding: '0 10px', border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-4)', fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', whiteSpace: 'nowrap', boxSizing: 'border-box', cursor: 'pointer' }}
                >
                  GAME PAGE ▸
                </div>
                {g.steamOn ? (
                  <a
                    href={g.steam}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-hov-acc-box"
                    style={{ display: 'flex', alignItems: 'center', height: 30, padding: '0 10px', border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-4)', fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', whiteSpace: 'nowrap', boxSizing: 'border-box', textDecoration: 'none' }}
                  >
                    STEAM ↗
                  </a>
                ) : null}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--gt-line)', paddingTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 0 6px' }}>
              <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', color: 'var(--gt-muted-2)' }}>PARTY</span>
              <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', color: 'var(--gt-muted-2)' }}>{g.qHead}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 18 }}>
              {g.party.map((w) => (
                <div key={w.key} style={{ display: 'flex', alignItems: 'center', gap: 8, height: 30, borderBottom: '1px solid var(--gt-line-0)' }}>
                  <SchFace f={w} px={16} fs={6} />
                  <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.05em', color: w.clr }}>{w.nm}</span>
                  <span style={{ marginLeft: 'auto', fontFamily: PIXEL, fontSize: 8, color: w.cClr, transform: 'translateY(0.13em)' }}>{w.call}</span>
                  <div style={{ width: 18, flex: 'none', display: 'flex', justifyContent: 'center' }}>{w.q ? <QuarterIcon still u={1} /> : null}</div>
                </div>
              ))}
            </div>
          </div>
          {g.editOn ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, border: '1px solid var(--acc)', background: 'var(--gt-field)', padding: 12 }}>
              <RangeSlider sl={g.sl} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <div onClick={() => s.schEditTgl()} style={{ display: 'flex', alignItems: 'center', height: 34, padding: '0 12px', border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-5)', fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', cursor: 'pointer', boxSizing: 'border-box' }}>
                    CANCEL
                  </div>
                  <PixelButton label="SAVE ▸" tone="acc" variant="solid" fs={8} pad="11px 14px" nowrap fit lh="1" busy={s.schBusy} onClick={() => void s.schEditSave()} />
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', borderTop: '1px solid var(--gt-line)', background: 'var(--gt-bg-hd)', flex: 'none', flexWrap: 'wrap' }}>
          {g.qBtnOn ? (
            <div
              onClick={() => void s.schQuarter(g.id)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, height: 38, padding: '0 14px', border: '1px ' + g.qBst + ' ' + g.qBd, background: g.qBg, color: g.qClr, fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', cursor: s.schBusy ? 'progress' : 'pointer', whiteSpace: 'nowrap', boxSizing: 'border-box' }}
            >
              {g.qMine ? <QuarterIcon still u={1.5} /> : <CoinOff px={21} />}
              <span>{g.qLbl}</span>
            </div>
          ) : null}
          {g.mine ? (
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <div
                onClick={() => s.schEditTgl()}
                className="d-hov-acc-box"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '9px 12px', border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-4)', fontFamily: MONO, fontSize: 10, letterSpacing: '.05em', cursor: 'pointer', whiteSpace: 'nowrap', boxSizing: 'border-box' }}
              >
                {g.editLbl}
              </div>
              <div
                onClick={() => void s.schRemove()}
                data-arm="scrm"
                data-tip={g.rmTip}
                style={{ overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '9px 12px', border: '1px solid ' + g.rmBd, background: g.rmBg, color: g.rmClr, fontFamily: MONO, fontSize: 10, letterSpacing: '.05em', cursor: 'pointer', whiteSpace: 'nowrap', boxSizing: 'border-box' }}
              >
                <ArmedLabel rest="✕ REMOVE" restV={g.rmArmed ? 'hidden' : 'visible'} armV={g.rmArmed ? 'visible' : 'hidden'} fill={g.rmFill} anim={g.rmArmed} />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function PoolTile({ p }: { p: SchNightVM['pool'][number] }): JSX.Element {
  const pick = useQd((s) => s.schSetPick);
  const art = useArt(p.id + 'p', p.coverCands);
  return (
    <div onClick={() => pick(p.on ? 0 : p.id)} style={{ display: 'flex', flexDirection: 'column', border: '1px solid ' + p.bd, background: 'var(--gt-tile)', cursor: 'pointer', boxShadow: p.sh }}>
      <div style={{ position: 'relative', aspectRatio: '2/3', background: p.fall, overflow: 'hidden' }}>
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={11} ini={p.ini} hasCover={art.hasCover} cover={art.cover} />
        <div style={{ position: 'absolute', left: 0, top: 0, zIndex: 2, background: 'var(--gt-ink)', color: p.tClr, fontFamily: PIXEL, fontSize: 7, padding: '4px 4px 2px' }}>{p.tLet}</div>
        {p.on ? (
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--acc)', color: 'var(--gt-ink)', fontFamily: MONO, fontSize: 8, letterSpacing: '.06em', padding: '4px 0' }}>PICKED</div>
        ) : null}
      </div>
      <div style={{ padding: '6px 7px', fontFamily: MONO, fontSize: '8.5px', color: 'var(--gt-text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nm}</div>
    </div>
  );
}

function NominateModal({ d }: { d: SchNightVM }): JSX.Element {
  const s = useQd();
  const close = () => s.schNomClose();
  useEscape(close);
  return (
    <div onClick={close} data-screen-label="Nominate" style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'var(--gt-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={stop} style={{ width: 640, maxWidth: '100%', height: 'min(720px,100%)', overflow: 'hidden', background: 'var(--gt-card)', border: '1px solid var(--gt-border)', boxShadow: 'var(--gt-shadow-modal)', display: 'flex', flexDirection: 'column', cursor: 'default' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderBottom: '1px solid var(--gt-line)', flex: 'none' }}>
          <span style={{ fontFamily: PIXEL, fontSize: '9.5px', color: 'var(--acc)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap' }}>NOMINATE</span>
          <span style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-muted)', whiteSpace: 'nowrap' }}>{d.ttl}</span>
          <span onClick={close} className="d-hov-acc2" style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 13, color: 'var(--gt-text-5)', cursor: 'pointer', padding: '2px 4px' }}>
            ✕
          </span>
        </div>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '14px 18px 12px', borderBottom: '1px solid var(--gt-line)' }}>
          <SearchBox value={d.pq} onChange={(e) => s.schSetQ(e.target.value)} onClear={() => s.schSetQ('')} placeholder="SEARCH THE LIST…" w="220px" pfs={10.5} ifs={10} pad="7px 10px" />
          <div style={{ display: 'flex', flex: 'none' }}>
            {d.tabs.map((t) => (
              <div
                key={t.key}
                onClick={() => s.schSetTier(t.key)}
                style={{ marginLeft: t.ml, position: 'relative', zIndex: t.z, display: 'flex', alignItems: 'center', gap: 5, height: 32, padding: '0 10px', border: '1px solid ' + t.bd, background: t.bg, color: t.clr, fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', cursor: 'pointer', whiteSpace: 'nowrap', boxSizing: 'border-box' }}
              >
                {t.t}
                <span style={{ color: t.nClr }}>{t.n}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 18px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(96px,1fr))', gap: 10 }}>
            {d.pool.map((p) => (
              <PoolTile key={p.key} p={p} />
            ))}
            {d.poolEmpty ? (
              <div style={{ gridColumn: '1/-1', border: '1px dashed var(--gt-border-3)', padding: 16, textAlign: 'center', fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-muted)' }}>{d.poolEmptyTxt}</div>
            ) : null}
          </div>
        </div>
        <div style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 18px 16px', borderTop: '1px solid var(--gt-line)', background: 'var(--gt-bg-hd)' }}>
          <RangeSlider sl={d.sl} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.06em', color: d.pickClr }}>{d.pickTxt}</span>
            <div style={{ marginLeft: 'auto', opacity: d.addOp, pointerEvents: d.addOk ? 'auto' : 'none' }}>
              <PixelButton label="ADD ▸" tone="acc" variant="solid" fs={8} pad="12px 16px" nowrap fit lh="1" busy={s.schBusy} onClick={() => void s.schNomAdd()} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
